/** 搜索匹配模式：模糊（包含）/ 精确（独立词，无前缀无后缀）——位置框、房间列表等 */
export const SEARCH_MATCH_MODE_OPTIONS = [
  { value: 'fuzzy', label: '模糊' },
  { value: 'exact', label: '精确' },
];

/** 电表主框：前缀（码类从左匹配；名称类仍按包含）/ 精确；value 仍传 fuzzy|exact 兼容后端 */
export const DEVICE_CODE_MATCH_MODE_OPTIONS = [
  { value: 'fuzzy', label: '前缀' },
  { value: 'exact', label: '精确' },
];

/** PC 操作记录：表号匹配（默认前缀） */
export const OPER_LOG_METER_MATCH_OPTIONS = [
  { value: 'prefix', label: '前缀' },
  { value: 'exact', label: '精确' },
];

/** PC 操作记录：搜索字段（默认表号） */
export const OPER_LOG_SEARCH_FIELD_OPTIONS = [
  { value: 'meter_no', label: '表号' },
  { value: 'device_id', label: '设备ID' },
];

/** PC 订单管理：搜索字段（默认表号）；表号路径会预查设备以兼容订单 meter_no 存 MAC */
export const ORDER_SEARCH_FIELD_OPTIONS = [
  { value: 'meter_no', label: '表号' },
  { value: 'user_phone', label: '手机号' },
  { value: 'house_name', label: '房源名' },
  { value: 'order_no', label: '订单号' },
  { value: 'order_id', label: '订单ID' },
  { value: 'user_id', label: '用户ID' },
  { value: 'device_id', label: '设备ID' },
  { value: 'room_id', label: '房间ID' },
];

/** PC 订单管理：表号/手机号/订单号/房源名 匹配模式 */
export const ORDER_SEARCH_MATCH_OPTIONS = [
  { value: 'prefix', label: '前缀' },
  { value: 'exact', label: '精确' },
];

const ORDER_SEARCH_PLACEHOLDER = {
  meter_no: '输入表号/MAC搜索',
  user_phone: '输入手机号搜索',
  house_name: '输入房源名搜索',
  order_no: '输入订单号搜索',
  order_id: '输入订单ID（数字）',
  user_id: '输入用户ID（数字）',
  device_id: '输入设备ID（数字）',
  room_id: '输入房间ID（数字）',
};

export function getOrderSearchPlaceholder(searchField) {
  return ORDER_SEARCH_PLACEHOLDER[searchField] || '输入关键词搜索';
}

/** 订单搜索：这些字段显示前缀/精确切换 */
export function orderSearchShowsMatchMode(searchField) {
  return ['meter_no', 'user_phone', 'house_name', 'order_no'].includes(searchField);
}

/** 可与关键词“续写”的字符（中文、字母、数字） */
function isContinuableChar(ch) {
  return /[\u4e00-\u9fffA-Za-z0-9]/.test(ch);
}

/** 与后端 normalizeLookupKey 对齐：去空白/冒号/横线并大写 */
function normalizeDeviceCodeKey(s) {
  return String(s ?? '')
    .trim()
    .replace(/[\s:\-]/g, '')
    .toUpperCase();
}

/** 表号/MAC/ICCID 等：精确 = 字段值全等（优先按归一化比） */
function matchExactEqual(value, keyword) {
  if (value == null || value === '') return false;
  const v = normalizeDeviceCodeKey(value);
  const kw = normalizeDeviceCodeKey(keyword);
  if (v && kw) return v === kw;
  return String(value).trim() === String(keyword).trim();
}

/** 码类非精确：前缀匹配（对齐后端 LIKE 'kw%'） */
function matchCodePrefix(value, keyword) {
  if (value == null || value === '') return false;
  const v = normalizeDeviceCodeKey(value);
  const kw = normalizeDeviceCodeKey(keyword);
  if (!kw) return false;
  return v.startsWith(kw);
}

/**
 * 房源/房间等名称：精确 = 关键词独立出现，前后不能紧贴中文/字母/数字
 * 例：B栋 可匹配「B栋」，不匹配「阳光B栋」「AB栋」「B栋101」
 */
function matchExactNoAffix(value, keyword) {
  if (value == null || value === '') return false;
  const s = String(value).trim();
  const kw = String(keyword).trim();
  if (!kw) return false;
  if (s === kw) return true;

  let from = 0;
  while (from <= s.length - kw.length) {
    const idx = s.indexOf(kw, from);
    if (idx === -1) return false;
    const before = idx > 0 ? s[idx - 1] : '';
    const after = idx + kw.length < s.length ? s[idx + kw.length] : '';
    const hasPrefix = before !== '' && isContinuableChar(before);
    const hasSuffix = after !== '' && isContinuableChar(after);
    if (!hasPrefix && !hasSuffix) return true;
    from = idx + 1;
  }
  return false;
}

function matchField(value, keyword, mode = 'fuzzy', fieldKind = 'code') {
  if (value == null || value === '') return false;
  if (mode === 'exact') {
    return fieldKind === 'name'
      ? matchExactNoAffix(value, keyword)
      : matchExactEqual(value, keyword);
  }
  // 非精确：码类=前缀；名称=包含（与电表后端一致）
  if (fieldKind === 'code') {
    return matchCodePrefix(value, keyword);
  }
  return String(value).includes(String(keyword).trim());
}

function matchText(value, keyword, matchMode = 'fuzzy', fieldKind = 'code') {
  return matchField(value, keyword, matchMode, fieldKind);
}

/** 房间列表：搜索范围选项 */
export const ROOM_SEARCH_SCOPE_OPTIONS = [
  { value: 'all', label: '全部字段' },
  { value: 'houseName', label: '房源名称' },
  { value: 'number', label: '房间号' },
  { value: 'tenantPhone', label: '租客手机号' },
  { value: 'houseId', label: '房源ID' },
  { value: 'roomId', label: '房间ID' },
  { value: 'tenantId', label: '租客ID' },
];

const ROOM_SEARCH_PLACEHOLDERS = {
  all: '搜索房源名称、房间号、租客手机号或ID',
  number: '请输入房间号，如：1302',
  houseName: '请输入房源名称，如：13栋',
  houseId: '请输入完整房源ID，如：13',
  roomId: '请输入完整房间ID，如：100',
  tenantId: '请输入完整租客用户ID',
  tenantPhone: '请输入租客手机号',
};

export function getRoomSearchPlaceholder(scope) {
  return ROOM_SEARCH_PLACEHOLDERS[scope] || ROOM_SEARCH_PLACEHOLDERS.all;
}

function matchId(value, keyword) {
  if (value == null || value === '') return false;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return false;
  return String(n) === keyword;
}

function isValidTenantPhone(phone) {
  if (phone == null || phone === '') return false;
  const s = String(phone).trim();
  return s !== '' && s !== '暂无租客';
}

function roomMatchesScope(room, keyword, scope, matchMode = 'fuzzy') {
  const mode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  switch (scope) {
    case 'number':
      return matchText(room.number, keyword, mode, 'name');
    case 'houseName':
      return matchText(room.houseName, keyword, mode, 'name');
    case 'houseId':
      return matchId(room.houseId, keyword);
    case 'roomId':
      return matchId(room.id, keyword);
    case 'tenantId':
      return matchId(room.tenantId, keyword);
    case 'tenantPhone':
      return isValidTenantPhone(room.tenantPhone) && matchText(room.tenantPhone, keyword, mode, 'code');
    case 'all':
    default:
      return (
        matchText(room.number, keyword, mode, 'name') ||
        matchText(room.houseName, keyword, mode, 'name') ||
        matchId(room.houseId, keyword) ||
        matchId(room.id, keyword) ||
        matchId(room.tenantId, keyword) ||
        (isValidTenantPhone(room.tenantPhone) && matchText(room.tenantPhone, keyword, mode, 'code'))
      );
  }
}

/** 按搜索范围过滤房间列表（纯前端） */
export function filterRoomList(rooms, keyword, scope = 'all', matchMode = 'fuzzy') {
  const kw = String(keyword ?? '').trim();
  if (!kw || !Array.isArray(rooms)) return rooms || [];
  const effectiveScope = scope || 'all';
  const effectiveMode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  return rooms.filter((room) => roomMatchesScope(room, kw, effectiveScope, effectiveMode));
}

/** 房源列表搜索（纯前端） */
export function filterHouseList(houses, keyword, matchMode = 'fuzzy') {
  const kw = String(keyword ?? '').trim();
  if (!kw || !Array.isArray(houses)) return houses || [];
  const mode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  return houses.filter((house) => {
    if (
      matchText(house.name, kw, mode, 'name') ||
      matchText(house.address, kw, mode, 'name') ||
      (mode === 'fuzzy' && house.landlordId?.toString().includes(kw)) ||
      (mode === 'exact' && house.landlordId?.toString() === kw) ||
      matchText(house.landlordPhone, kw, mode, 'code')
    ) {
      return true;
    }
    const admins = house.admins || [];
    return admins.some(
      (a) =>
        matchText(a.phone, kw, mode, 'code') || matchText(a.nickname, kw, mode, 'name'),
    );
  });
}

/** 水表/门锁：搜索范围选项 */
export const DEVICE_LIST_SEARCH_SCOPE_OPTIONS = [
  { value: 'all', label: '全部字段' },
  { value: 'current_meter_no', label: '表号' },
  { value: 'house_name', label: '所属房源' },
  { value: 'device_mac', label: '设备MAC' },
  { value: 'room_name', label: '绑定房间' },
  { value: 'factory_meter_no', label: '厂商表号' },
  { value: 'iccid', label: 'ICCID' },
  { value: 'imei', label: 'IMEI' },
];

const DEVICE_LIST_SEARCH_PLACEHOLDERS = {
  all: '搜索表号、房源、MAC、房间、厂商表号、ICCID或IMEI',
  current_meter_no: '请输入表号',
  house_name: '请输入所属房源名称，如：1栋',
  device_mac: '请输入设备MAC',
  room_name: '请输入绑定房间号',
  factory_meter_no: '请输入厂商表号',
  iccid: '请输入ICCID',
  imei: '请输入IMEI',
};

export function getDeviceListSearchPlaceholder(scope) {
  return DEVICE_LIST_SEARCH_PLACEHOLDERS[scope] || DEVICE_LIST_SEARCH_PLACEHOLDERS.all;
}

function deviceMatchesScope(device, keyword, scope, matchMode = 'fuzzy', extraFields = []) {
  const mode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  const matchCode = (value) => matchText(value, keyword, mode, 'code');
  const matchName = (value) => matchText(value, keyword, mode, 'name');
  switch (scope) {
    case 'device_mac':
      return matchCode(device.device_mac);
    case 'factory_meter_no':
      return matchCode(device.factory_meter_no);
    case 'current_meter_no':
      return matchCode(device.current_meter_no);
    case 'iccid':
      return matchCode(device.iccid);
    case 'imei':
      return matchCode(device.imei);
    case 'room_name':
      return matchName(device.room_name);
    case 'house_name':
      return matchName(device.house_name);
    case 'all':
    default:
      return (
        matchCode(device.device_mac) ||
        matchCode(device.factory_meter_no) ||
        matchCode(device.current_meter_no) ||
        matchCode(device.iccid) ||
        matchCode(device.imei) ||
        matchName(device.room_name) ||
        matchName(device.house_name) ||
        extraFields.some((field) => matchCode(device[field]))
      );
  }
}

/** 设备列表：位置二次筛选范围（房间/房源） */
export const DEVICE_LOCATION_SEARCH_SCOPE_OPTIONS = [
  { value: 'all', label: '全部字段' },
  { value: 'room_name', label: '绑定房间' },
  { value: 'house_name', label: '所属房源' },
];

const DEVICE_LOCATION_SEARCH_PLACEHOLDERS = {
  all: '筛选绑定房间或所属房源（模糊），如：101、1栋',
  room_name: '请输入绑定房间号（模糊），如：101',
  house_name: '请输入所属房源名称（模糊），如：1栋',
};

export function getDeviceLocationSearchPlaceholder(scope) {
  return DEVICE_LOCATION_SEARCH_PLACEHOLDERS[scope] || DEVICE_LOCATION_SEARCH_PLACEHOLDERS.all;
}

function deviceMatchesLocationScope(device, keyword, scope, matchMode = 'fuzzy') {
  const mode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  const matchName = (value) => matchText(value, keyword, mode, 'name');
  switch (scope) {
    case 'room_name':
      return matchName(device.room_name);
    case 'house_name':
      return matchName(device.house_name);
    case 'all':
    default:
      return matchName(device.room_name) || matchName(device.house_name);
  }
}

/** 水表/门锁：按绑定位置二次筛选（纯前端；keyword 为空时不筛选） */
export function filterDeviceListByLocation(devices, keyword, scope = 'all', matchMode = 'fuzzy') {
  const kw = String(keyword ?? '').trim();
  if (!kw || !Array.isArray(devices)) return devices || [];
  const effectiveScope = scope || 'all';
  const effectiveMode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  return devices.filter((device) =>
    deviceMatchesLocationScope(device, kw, effectiveScope, effectiveMode),
  );
}

/** 水表/门锁列表过滤（纯前端） */
export function filterDeviceList(devices, keyword, scope = 'all', matchMode = 'fuzzy', extraFields = []) {
  const kw = String(keyword ?? '').trim();
  if (!kw || !Array.isArray(devices)) return devices || [];
  const effectiveScope = scope || 'all';
  const effectiveMode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  return devices.filter((device) =>
    deviceMatchesScope(device, kw, effectiveScope, effectiveMode, extraFields),
  );
}

/** 电表列表：搜索范围选项（已去掉无效的最新RX主题） */
export const ELECTRIC_METER_SEARCH_SCOPE_OPTIONS = [
  { value: 'all', label: '全部字段' },
  { value: 'current_meter_no', label: '表号' },
  { value: 'house_name', label: '所属房源' },
  { value: 'device_mac', label: '设备MAC' },
  { value: 'room_name', label: '绑定房间' },
  { value: 'factory_meter_no', label: '厂商表号' },
  { value: 'iccid', label: 'ICCID' },
  { value: 'imei', label: 'IMEI' },
];

const ELECTRIC_METER_SEARCH_PLACEHOLDERS = {
  all: '搜索表号、房源、MAC、房间、厂商表号、ICCID、IMEI（前缀）',
  current_meter_no: '请输入表号（前缀）',
  house_name: '请输入所属房源名称（模糊），如：1栋',
  device_mac: '请输入设备MAC（前缀）',
  room_name: '请输入绑定房间号（模糊）',
  factory_meter_no: '请输入厂商表号（前缀）',
  iccid: '请输入ICCID（前缀）',
  imei: '请输入IMEI（前缀）',
};

export function getElectricMeterSearchPlaceholder(scope) {
  return ELECTRIC_METER_SEARCH_PLACEHOLDERS[scope] || ELECTRIC_METER_SEARCH_PLACEHOLDERS.all;
}

function deviceMatchesElectricScope(device, keyword, scope, matchMode = 'fuzzy') {
  const mode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  // 主框「前缀」模式下：码类前缀；名称类仍包含（与后端隐藏规则一致）
  const matchCode = (value) => matchText(value, keyword, mode, 'code');
  const matchName = (value) => matchText(value, keyword, mode === 'exact' ? 'exact' : 'fuzzy', 'name');
  switch (scope) {
    case 'device_mac':
      return matchCode(device.device_mac);
    case 'factory_meter_no':
      return matchCode(device.factory_meter_no);
    case 'current_meter_no':
      return matchCode(device.current_meter_no);
    case 'iccid':
      return matchCode(device.iccid);
    case 'imei':
      return matchCode(device.imei);
    case 'room_name':
      return matchName(device.room_name);
    case 'house_name':
      return matchName(device.house_name);
    case 'all':
    default:
      return (
        matchCode(device.device_mac) ||
        matchCode(device.factory_meter_no) ||
        matchCode(device.current_meter_no) ||
        matchCode(device.iccid) ||
        matchCode(device.imei) ||
        matchName(device.room_name) ||
        matchName(device.house_name)
      );
  }
}

/** 按搜索范围过滤电表列表（纯前端，关键词部分） */
export function filterElectricMeterList(devices, keyword, scope = 'all', matchMode = 'fuzzy') {
  const kw = String(keyword ?? '').trim();
  if (!kw || !Array.isArray(devices)) return devices || [];
  const effectiveScope = scope || 'all';
  const effectiveMode = matchMode === 'exact' ? 'exact' : 'fuzzy';
  return devices.filter((device) => deviceMatchesElectricScope(device, kw, effectiveScope, effectiveMode));
}

/**
 * 电表结构性筛选（与关键词搜索分离）
 * filters: { communicationType, onlineStatus, detailType, deviceStatus }
 * 各字段为空字符串时不限制
 */
export function applyElectricMeterFilters(devices, filters = {}) {
  if (!Array.isArray(devices)) return [];
  const {
    communicationType = '',
    onlineStatus = '',
    detailType = '',
    deviceStatus = '',
  } = filters;
  return devices.filter((device) => {
    if (communicationType !== '') {
      if (String(device.communication_type ?? '') !== String(communicationType)) return false;
    }
    if (onlineStatus !== '') {
      if (device.is_online == null) return false;
      if (String(device.is_online) !== String(onlineStatus)) return false;
    }
    if (detailType !== '') {
      if (String(device.device_detail_type ?? '') !== String(detailType)) return false;
    }
    if (deviceStatus !== '') {
      if (String(device.device_status ?? '') !== String(deviceStatus)) return false;
    }
    return true;
  });
}

/** 关键词搜索 + 结构性筛选 */
export function filterAndSearchElectricMeters(devices, keyword, scope, filters) {
  let list = filterElectricMeterList(devices, keyword, scope);
  list = applyElectricMeterFilters(list, filters);
  return list;
}
